// Canonical exercise identity helpers.
//
// Display names remain untouched. Matching uses a stable key so punctuation,
// spacing and common plural variants do not split one movement into multiple
// identities.

const PLURAL_TOKEN_ALIASES = new Map([
  ['presses', 'press'],
  ['rows', 'row'],
  ['curls', 'curl'],
  ['raises', 'raise'],
  ['extensions', 'extension'],
  ['squats', 'squat'],
  ['lunges', 'lunge'],
  ['deadlifts', 'deadlift'],
  ['burpees', 'burpee'],
  ['crunches', 'crunch'],
  ['dips', 'dip'],
  ['pulldowns', 'pulldown'],
  ['swings', 'swing'],
  ['climbers', 'climber'],
  ['jacks', 'jack'],
  ['flyes', 'fly'],
  ['flies', 'fly'],
]);

export const normalizeExerciseName = (value = '') => {
  let normalized = String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[’'`]/g, '')
    .replace(/[-_/]+/g, ' ')
    .replace(/[()[\]{}.,:;!?]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  normalized = normalized
    .replace(/\bpullups?\b/g, 'pull up')
    .replace(/\bpushups?\b/g, 'push up')
    .replace(/\bchinups?\b/g, 'chin up')
    .replace(/\bsitups?\b/g, 'sit up')
    .replace(/\bstepups?\b/g, 'step up');

  const tokens = normalized.split(' ').filter(Boolean);

  for (let i = 1; i < tokens.length; i += 1) {
    if (
      tokens[i] === 'ups' &&
      ['pull', 'push', 'chin', 'sit', 'step'].includes(tokens[i - 1])
    ) {
      tokens[i] = 'up';
    }
  }

  // Conservative exercise-specific plurals only. Do not singularize generic
  // anatomy words such as "legs" or "arms".
  for (let i = 0; i < tokens.length; i += 1) {
    if (PLURAL_TOKEN_ALIASES.has(tokens[i])) {
      tokens[i] = PLURAL_TOKEN_ALIASES.get(tokens[i]);
    }
  }

  return tokens.join(' ');
};

export const exerciseNamesMatch = (left, right) =>
  normalizeExerciseName(left) === normalizeExerciseName(right);

export const findExerciseByName = (exercises = [], name = '') => {
  const target = normalizeExerciseName(name);
  if (!target) return null;

  return exercises.find((exercise) => {
    const candidate = exercise?.exercise_name || exercise?.name || '';
    return normalizeExerciseName(candidate) === target;
  }) || null;
};

export const classifyExercise = (exercise = {}) => {
  const name = normalizeExerciseName(exercise.exercise_name || exercise.name || '');
  const muscle = normalizeExerciseName(exercise.muscle_group || exercise.muscle || '');
  const category = normalizeExerciseName(exercise.category || exercise.type || '');
  const equipment = normalizeExerciseName(exercise.equipment || '');
  const repsText = String(exercise.recommended_reps ?? exercise.reps ?? '').toLowerCase();

  const isWarmup =
    exercise.is_warmup === true ||
    muscle.includes('warm') ||
    category.includes('warm') ||
    name.includes('warm up');

  const isCooldown =
    exercise.is_cooldown === true ||
    muscle.includes('cool down') ||
    category.includes('cool down') ||
    name.includes('cool down');

  const cardioByName = [
    'jumping jack', 'burpee', 'mountain climber', 'high knee', 'jump rope',
    'running', 'treadmill', 'cycling', 'stationary bike', 'exercise bike',
    'elliptical', 'rowing machine', 'rower', 'stair climber',
  ].some((term) => name.includes(term));

  const isCardio =
    muscle.includes('cardio') ||
    category.includes('cardio') ||
    isWarmup ||
    cardioByName;

  const isTimed =
    exercise.is_timed === true ||
    exercise.type === 'timed' ||
    repsText.includes('hold') ||
    repsText.includes('second') ||
    repsText.includes('minute') ||
    ['hold', 'pose', 'plank', 'stretch', 'yoga', 'pilates'].some((term) => name.includes(term)) ||
    ['yoga', 'pilates', 'stretch', 'recovery', 'balance', 'relaxation'].some(
      (term) => muscle.includes(term) || category.includes(term)
    );

  const isMachineCardio =
    isCardio &&
    [
      'treadmill', 'stationary bike', 'exercise bike', 'cycling', 'elliptical',
      'rowing machine', 'rower', 'stair climber',
    ].some((term) => name.includes(term));

  const explicitBodyweight = ['bodyweight', 'body weight', 'body only', 'none'].includes(equipment);
  const weightedName = ['dumbbell', 'barbell', 'kettlebell', 'cable', 'machine', 'weighted'].some(
    (term) => name.includes(term)
  );

  const bodyweightByName =
    ['push up', 'pull up', 'chin up', 'burpee', 'jumping jack', 'mountain climber', 'crunch', 'sit up', 'leg raise']
      .some((term) => name.includes(term)) ||
    (name.includes('dip') && !weightedName) ||
    (name.includes('lunge') && !weightedName) ||
    (name.includes('squat') && !weightedName);

  const isBodyweight = explicitBodyweight || bodyweightByName;

  let defaultDuration = Number(exercise.duration_seconds) || 0;
  if (isTimed && !defaultDuration) {
    const secMatch = repsText.match(/(\d+)\s*sec/);
    const minMatch = repsText.match(/(\d+)\s*min/);
    if (secMatch) defaultDuration = Number(secMatch[1]);
    else if (minMatch) defaultDuration = Number(minMatch[1]) * 60;
    else defaultDuration = 30;
  }

  return {
    normalized_name: name,
    is_cardio: isCardio,
    is_machine_cardio: isMachineCardio,
    is_timed: isTimed,
    is_bodyweight: isBodyweight,
    is_warmup: isWarmup,
    is_cooldown: isCooldown,
    recommended_time: isWarmup || isCooldown ? 10 : 30,
    default_duration: defaultDuration,
  };
};
